import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useExport } from './header/exporters';
import idColumn from './ui/idColumn';
import DataTable, { Column } from './DataTable';
import EmptyState from './EmptyState';
import Icon from './Icon';
import { MetricCard } from './MetricCards';
import { topGenres, songCountLabel } from '../utils/musicGenres';
import ConfirmDialog from './ui/ConfirmDialog';
import { getGuidelinesByCategory } from '../services/GuidelinesService';
import { loadSongs, archiveSong, restoreSong, updateSong, refreshSongsFromApi, Song } from '../services/SongsService';
import { useNotifications } from './NotificationProvider';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import '../styles/components/MenuView.css';
import '../styles/components/MusicView.css';

const tabs = ['After Party', 'Tunnel Music', 'Church Hymns', 'Guidelines'];

export type { Song };

export const GENRES = ['Afrobeats', 'Soul', 'RnB', 'Rock', 'Alternative', 'Jazz', 'Pop', 'Latin', 'Reggae', 'Classical', 'Hymn', 'Contemporary Christian'] as const;
export const SONG_TYPES = ['After Party', 'Tunnel Music', 'Church Hymns'] as const;

const genreColors: Record<string, string> = {
  Afrobeats: '#e65100', Soul: '#6a1b9a', RnB: '#1565c0', Rock: '#37474f',
  Alternative: '#2e7d32', Jazz: '#4a148c', Pop: '#880e4f', Latin: '#b71c1c',
  Reggae: '#1b5e20', Classical: '#3e2723', Hymn: '#01579b', 'Contemporary Christian': '#006064',
};

export const MOMENTS = [
  { moment: 'Cocktail Hour',            desc: 'James Bond themed cocktails, background music' },
  { moment: 'MC Introductions',         desc: 'Background music during MC welcome' },
  { moment: 'Bridal Party Intro',       desc: 'Upbeat entrance music for wedding party' },
  { moment: 'Grand Entrance',           desc: 'Mission Impossible theme, military honours' },
  { moment: 'Dinner – Band',            desc: "Gentleman's era classics, elegant music" },
  { moment: 'Band → DJ Transition',     desc: 'Band transitions to DJ during last song' },
  { moment: 'First Dance',              desc: "Couple's first dance song" },
  { moment: 'Speeches',                 desc: 'Background music during toasts' },
  { moment: 'Cake Cutting',             desc: 'Celebratory music' },
  { moment: 'Dancing',                  desc: "Gentleman's era classics" },
  { moment: 'Venue Transformation',     desc: 'Transition music between reception and after party' },
  { moment: 'After Party DJ',           desc: 'Tokyo Neon + Pandora themes' },
  { moment: 'Grand Finale',             desc: 'Exit music — sparklers' },
];

// ── DJ Brief Modal ────────────────────────────────────────────────────────────

interface DJBriefProps {
  songs: Song[];
  onClose: () => void;
}

const DJBrief: React.FC<DJBriefProps> = ({ songs, onClose }) => {
  const momentSongs = (moment: string) => songs.filter(s => s.moment === moment);
  const byType = (type: string) => songs.filter(s => s.type === type && !s.moment);
  const nonNeg = songs.filter(s => s.non_negotiable);

  const handlePrint = () => window.print();
  const handleCopy = () => {
    const lines: string[] = [
      'EKŌCE WEDDING — DJ/MEDIA BRIEF',
      'December 5, 2026',
      '',
      '━━ RECEPTION TIMELINE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '',
    ];
    MOMENTS.forEach(({ moment, desc }) => {
      lines.push(`▸ ${moment}`);
      lines.push(`  ${desc}`);
      const pinned = momentSongs(moment);
      if (pinned.length) {
        pinned.forEach(s => {
          const flag = s.non_negotiable ? ' ● NON-NEGOTIABLE' : '';
          lines.push(`  → ${s.name}${s.artist ? ` — ${s.artist}` : ''}${flag}`);
        });
      }
      lines.push('');
    });

    if (nonNeg.length) {
      lines.push('━━ NON-NEGOTIABLES ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      lines.push('');
      nonNeg.forEach(s => lines.push(`  ● ${s.name}${s.artist ? ` — ${s.artist}` : ''} (${s.type}${s.moment ? ` / ${s.moment}` : ''})`));
      lines.push('');
    }

    (['After Party', 'Tunnel Music', 'Church Hymns'] as const).forEach(type => {
      const list = songs.filter(s => s.type === type);
      if (!list.length) return;
      lines.push(`━━ ${type.toUpperCase()} ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      lines.push('');
      list.forEach(s => {
        const flag = s.non_negotiable ? ' ●' : '';
        lines.push(`  • ${s.name}${s.artist ? ` — ${s.artist}` : ''}${flag}`);
      });
      lines.push('');
    });

    navigator.clipboard.writeText(lines.join('\n')).catch(() => {});
  };

  return (
    <div className="dj-brief-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dj-brief-modal">
        <div className="dj-brief-header">
          <div>
            <h2 className="dj-brief-title">DJ / Media Brief</h2>
            <p className="dj-brief-subtitle">EKŌCE Wedding · December 5, 2026</p>
          </div>
          <div className="dj-brief-actions">
            <button className="dj-brief-btn" onClick={handleCopy}>Copy text</button>
            <button className="dj-brief-btn" onClick={handlePrint}>Print</button>
            <button className="dj-brief-close" onClick={onClose} aria-label="Close"><Icon name="close" size={16} /></button>
          </div>
        </div>

        <div className="dj-brief-body">
          {/* Timeline */}
          <section className="dj-brief-section">
            <h3 className="dj-brief-section-title">Reception Timeline</h3>
            <div className="dj-moment-list">
              {MOMENTS.map(({ moment, desc }) => {
                const pinned = momentSongs(moment);
                return (
                  <div key={moment} className="dj-moment-item">
                    <div className="dj-moment-header">
                      <span className="dj-moment-name">{moment}</span>
                      <span className="dj-moment-desc">{desc}</span>
                    </div>
                    {pinned.length > 0 && (
                      <div className="dj-moment-songs">
                        {pinned.map(s => (
                          <span key={s.id ?? s.name} className={`dj-song-chip${s.non_negotiable ? ' dj-song-chip--nonneg' : ''}`}>
                            {s.non_negotiable && <span className="dj-nonneg-dot" title="Non-negotiable">●</span>}
                            {s.name}{s.artist ? ` — ${s.artist}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Non-negotiables summary */}
          {nonNeg.length > 0 && (
            <section className="dj-brief-section">
              <h3 className="dj-brief-section-title dj-brief-section-title--nonneg"><span className="dj-nonneg-dot" title="Non-negotiable">●</span> Non-Negotiables</h3>
              <ul className="dj-nonneg-list">
                {nonNeg.map(s => (
                  <li key={s.id ?? s.name}>
                    <strong>{s.name}</strong>{s.artist ? ` — ${s.artist}` : ''}
                    <span className="dj-nonneg-context">{s.type}{s.moment ? ` / ${s.moment}` : ''}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Playlists */}
          {(['After Party', 'Tunnel Music', 'Church Hymns'] as const).map(type => {
            const list = songs.filter(s => s.type === type);
            if (!list.length) return null;
            return (
              <section key={type} className="dj-brief-section">
                <h3 className="dj-brief-section-title">{type} <span className="dj-song-count">({list.length} songs)</span></h3>
                <ol className="dj-playlist">
                  {list.map((s, i) => (
                    <li key={s.id ?? s.name} className={s.non_negotiable ? 'dj-playlist-nonneg' : ''}>
                      <span className="dj-playlist-num">{i + 1}.</span>
                      <span className="dj-playlist-song">{s.name}{s.artist ? ` — ${s.artist}` : ''}</span>
                      {s.non_negotiable && <span className="dj-nonneg-dot" title="Non-negotiable">●</span>}
                      {s.link && <a href={s.link} target="_blank" rel="noopener noreferrer" className="play-link">▶</a>}
                    </li>
                  ))}
                </ol>
              </section>
            );
          })}

          {/* Unassigned type songs */}
          {(['After Party', 'Tunnel Music', 'Church Hymns'] as const).map(type => {
            const list = byType(type);
            return list.length ? null : null; // already handled above
          })}
        </div>
      </div>
    </div>
  );
};

// ── Moment row with song attachment ──────────────────────────────────────────

interface MomentRowProps {
  moment: string;
  desc: string;
  pinned: Song[];
  allSongs: Song[];
  onPin: (song: Song) => void;
  onUnpin: (song: Song) => void;
}

const MomentRow: React.FC<MomentRowProps> = ({ moment, desc, pinned, allSongs, onPin, onUnpin }) => {
  const [picking, setPicking] = useState(false);
  const unpinned = allSongs.filter(s => !s.moment);

  return (
    <div className="moment-row moment-row--interactive">
      <div className="moment-row-top">
        <span className="moment-label">{moment}</span>
        <span className="moment-desc">{desc}</span>
        <button className="moment-add-btn" onClick={() => setPicking(p => !p)} title="Attach a song">
          {picking ? '−' : '+'}
        </button>
      </div>

      {pinned.length > 0 && (
        <div className="moment-pinned-songs">
          {pinned.map(s => (
            <span key={s.id ?? s.name} className={`moment-song-chip${s.non_negotiable ? ' moment-song-chip--nonneg' : ''}`}>
              {s.non_negotiable && <span title="Non-negotiable">●</span>}
              {s.name}{s.artist ? ` — ${s.artist}` : ''}
              <button className="moment-song-unpin" onClick={() => onUnpin(s)} title="Unpin">×</button>
            </span>
          ))}
        </div>
      )}

      {picking && (
        <div className="moment-picker">
          {unpinned.length === 0
            ? <span className="moment-picker-empty">All songs already pinned to moments.</span>
            : unpinned.map(s => (
              <button
                key={s.id ?? s.name}
                className="moment-picker-song"
                onClick={() => { onPin(s); setPicking(false); }}
              >
                {s.name}{s.artist ? ` — ${s.artist}` : ''} <span className="moment-picker-type">({s.type})</span>
              </button>
            ))
          }
        </div>
      )}
    </div>
  );
};

// ── Song columns ──────────────────────────────────────────────────────────────

function buildSongColumns(
  onArchive: (song: Song) => void,
  onToggleNonNeg: (song: Song) => void,
  showingArchived: boolean,
  onRestore: (song: Song) => void,
): Column<Song>[] {
  return [
    idColumn<Song>('song'),
    {
      key: 'non_negotiable',
      header: '',
      width: '32px',
      align: 'center',
      render: (_, song) => (
        <button
          className={`nonneg-btn${song.non_negotiable ? ' nonneg-btn--on' : ''}`}
          title={song.non_negotiable ? 'Non-negotiable (click to unmark)' : 'Mark as non-negotiable'}
          onClick={e => { e.stopPropagation(); onToggleNonNeg(song); }}
        >
          ●
        </button>
      ),
    },
    {
      key: 'name',
      header: 'Song',
      render: (_, song) => (
        <div className="song-title-cell">
          <span className="song-name">{song.name}</span>
          {song.artist && <span className="song-artist">{song.artist}</span>}
          {song.moment && <span className="song-moment-tag">{song.moment}</span>}
          {song.notes && <span className="song-notes">{song.notes}</span>}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Category',
      width: '130px',
      render: (_, song) => {
        const typeColors: Record<string, string> = {
          'After Party': '#6d28d9',
          'Tunnel Music': '#0369a1',
          'Church Hymns': '#065f46',
        };
        const color = typeColors[song.type] || '#374151';
        return (
          <span
            className="genre-badge"
            // eslint-disable-next-line react/forbid-component-props
            style={{ /* ds-dynamic: category color from map */ background: color }}
          >
            {song.type}
          </span>
        );
      },
    },
    {
      key: 'genre',
      header: 'Genre',
      width: '130px',
      render: (_, song) => (
        <span
          className="genre-badge"
          // eslint-disable-next-line react/forbid-component-props
          style={{ /* ds-dynamic: genre color from map */ background: genreColors[song.genre] || '#374151' }}
        >
          {song.genre}
        </span>
      ),
    },
    {
      key: 'link',
      header: '',
      width: '60px',
      align: 'center',
      render: (_, song) => song.link
        ? <a href={song.link} target="_blank" rel="noopener noreferrer" className="play-link">▶ Play</a>
        : null,
    },
    {
      key: 'actions',
      header: '',
      width: showingArchived ? '80px' : '40px',
      align: 'center',
      render: (_, song) => (
        showingArchived ? (
          <button
            className="song-restore-btn"
            title="Restore song"
            onClick={e => { e.stopPropagation(); onRestore(song); }}
          >
            Restore
          </button>
        ) : (
          <button
            className="song-delete-btn"
            title="Archive song"
            onClick={e => { e.stopPropagation(); onArchive(song); }}
          >
            ×
          </button>
        )
      ),
    },
  ];
}

// ── Main component ────────────────────────────────────────────────────────────

interface MusicViewProps {
  musicTab?: string;
  onMusicTabChange?: (tab: string) => void;
}

const MusicView: React.FC<MusicViewProps> = ({ musicTab: externalTab, onMusicTabChange }) => {
  const [internalTab, setInternalTab] = useState('After Party');
  const activeTab = externalTab ?? internalTab;
  const setActiveTab = (tab: string) => { setInternalTab(tab); onMusicTabChange?.(tab); };

  const { showError, showSuccess } = useNotifications();
  const [songs, setSongs] = useState<Song[]>([]);
  const [showBrief, setShowBrief] = useState(false);
  const [archiveSongTarget, setArchiveSongTarget] = useState<Song | null>(null);
  // 3-state (active/archived/all), matching Venues — lives in Header's
  // breadcrumb instead of a button floating in the page body below it.
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');
  const searchTerm = useHeaderSearch();
  const musicGuidelines = useMemo(() => getGuidelinesByCategory('music'), []);

  useEffect(() => {
    setSongs(loadSongs());
    refreshSongsFromApi().then(refreshed => setSongs(refreshed));
  }, []);

  useEffect(() => {
    const handleFocus = () => setSongs(loadSongs());
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('music-archived-sync', { detail: archiveFilter }));
  }, [archiveFilter]);

  useEffect(() => {
    const onSet = (e: Event) => setArchiveFilter((e as CustomEvent).detail);
    window.addEventListener('music-archived-set', onSet);
    return () => window.removeEventListener('music-archived-set', onSet);
  }, []);

  const tabSongs = songs.filter(s => {
    if (s.type !== activeTab) return false;
    if (archiveFilter === 'active') return !s.archived;
    if (archiveFilter === 'archived') return !!s.archived;
    return true;
  }).filter(s => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      s.name.toLowerCase().includes(term) ||
      (s.artist?.toLowerCase().includes(term) ?? false) ||
      (s.genre?.toLowerCase().includes(term) ?? false) ||
      (s.moment?.toLowerCase().includes(term) ?? false) ||
      (s.notes?.toLowerCase().includes(term) ?? false)
    );
  });

  const handleArchiveSong = (song: Song) => {
    setArchiveSongTarget(song);
  };

  const handleRestoreSong = async (song: Song) => {
    if (!song.id) return;
    try {
      setSongs(await restoreSong(song.id));
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'song', action: 'restore' }));
    }
  };

  // Every edit below goes through the same shape: only paint the new list once
  // the server has taken the change, or the next refresh silently reverts it.
  const saveSong = useCallback(async (updated: Song) => {
    try {
      setSongs(await updateSong(updated));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save song.');
    }
  }, [showError]);

  const handleToggleNonNeg = useCallback((song: Song) => {
    saveSong({ ...song, non_negotiable: !song.non_negotiable });
  }, [saveSong]);

  const handlePinToMoment = useCallback((song: Song, moment: string) => {
    saveSong({ ...song, moment });
  }, [saveSong]);

  const handleUnpinFromMoment = useCallback((song: Song) => {
    saveSong({ ...song, moment: undefined });
  }, [saveSong]);

  useEffect(() => {
    const handler = () => setShowBrief(true);
    window.addEventListener('music-dj-brief', handler);
    return () => window.removeEventListener('music-dj-brief', handler);
  }, []);

  // Export button (Header): the songs on the open tab (the Guidelines tab has none).
  useExport('music', { rows: activeTab === 'Guidelines' ? [] : tabSongs, ctx: { tab: activeTab } });

  const songColumns = buildSongColumns(handleArchiveSong, handleToggleNonNeg, (archiveFilter === 'archived'), handleRestoreSong);

  return (
    <div className="wt-container fade-in">

        {activeTab === 'Tunnel Music' && (
          <div className="music-tunnel-warning">
            <strong>Note: Tunnel music is instrumentals ONLY — NO LYRICS.</strong> Band plays for 1hr 30min during tunnel entrance. Keep sound level consistent. Transition to DJ during last song.
          </div>
        )}

        {activeTab !== 'Guidelines' && (
          <>
            <section className="stats-grid">
              <MetricCard label="Songs" value={tabSongs.length} tone="purple" icon="music" />
              {/* The two most common genres in this playlist; they update as songs are added or removed. */}
              {topGenres(tabSongs).map((g, i) => (
                <MetricCard
                  key={g.genre}
                  label={i === 0 ? 'Top genre' : '2nd genre'}
                  value={g.genre}
                  tone={i === 0 ? 'pink' : 'blue'}
                  icon="music"
                  extra={songCountLabel(g.count)}
                  title={`${g.genre}: ${songCountLabel(g.count)} of ${tabSongs.length}`}
                />
              ))}
            </section>
            <DataTable
            pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
              data={tabSongs}
              columns={songColumns}
              hoverable
              compact
              emptyMessage={(archiveFilter === 'archived') ? 'No archived songs.' : 'No songs.'}
            />
          </>
        )}

        {activeTab === 'Guidelines' && (
          <div className="guidelines-section">
            {musicGuidelines.map(guideline => (
              <div key={guideline.id} className="guideline-card">
                <h3>{guideline.title}</h3>
                <ul className="guideline-list">
                  {guideline.items.map(item => (
                    <li key={item.id}>
                      {item.emphasis ? <strong>{item.text}</strong> : item.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className="guideline-card">
              <h3>Reception Music by Moment</h3>
              <p className="moment-hint">Pin songs to each moment to build the DJ's run-sheet. Tap + to attach.</p>
              <div className="moment-table">
                {MOMENTS.map(({ moment, desc }) => (
                  <MomentRow
                    key={moment}
                    moment={moment}
                    desc={desc}
                    pinned={songs.filter(s => s.moment === moment)}
                    allSongs={songs}
                    onPin={song => handlePinToMoment(song, moment)}
                    onUnpin={handleUnpinFromMoment}
                  />
                ))}
              </div>
            </div>

            {musicGuidelines.length === 0 && (
              <EmptyState title="No music guidelines yet." description="Add them from the Guidelines section." compact />
            )}
          </div>
        )}

      {showBrief && <DJBrief songs={songs} onClose={() => setShowBrief(false)} />}
      <ConfirmDialog
        open={archiveSongTarget !== null}
        onClose={() => setArchiveSongTarget(null)}
        onConfirm={async () => {
          const id = archiveSongTarget?.id;
          setArchiveSongTarget(null);
          if (!id) return;
          try {
            setSongs(await archiveSong(id));
          } catch (err) {
            showError(friendlyDeleteError(err, { record: 'song', action: 'archive' }));
          }
        }}
        title={`Archive "${archiveSongTarget?.name}"?`}
        description="It will be hidden from the active list but not permanently deleted. You can restore it later from the Archived filter."
        confirmLabel="Archive"
      />
    </div>
  );
};

export default MusicView;
